import CommonBlok from '@/components/common-blok';
import React from 'react';
import * as styles from './index.module.less';
import { useTranslation } from 'gatsby-plugin-react-i18next';
import { FreePlanInfo, PlanType, PricePlanType, ProPlanInfo, TeamsPlanInfo } from '../../constants';
import { Button, Caption, H2, H4 } from '@/components';
import { PublicCheckFilled } from 'notta-web-icon';
import { PublicArrowRightTail } from 'notta-web-icon';
import ButtonWithArrow from '@/components/button-with-arrow';
import PricingCardContainer from '@/modules/pricing-v2/pricing-card-container';
import PricingProvider from '@/modules/pricing-v2/provider';
import SwitchPayType from '@/modules/pricing-v2/switch-pay-type';

interface IProp {
  data: PricePlanType;
  gridArea: string;
}

const CARD_BG = {
  [PlanType.FREE]: {
    header: '#EEEFF0',
    body: '#F8F9FA',
  },
  [PlanType.PRO]: {
    header: '#F0F9FF',
    body: '#FFFFFF',
  },
  [PlanType.TEAMS]: {
    header: '#D6EFFF',
    body: '#F0F9FF',
  },
};

const PricingCardHeader = ({ data, gridArea }: IProp) => {
  const { t } = useTranslation();
  return (
    <div className={styles.card_header_container} style={{ background: CARD_BG[data.type].header, gridArea }}>
      <div className={styles.card_header_name_tag}>
        <H4>{t(data.name)}</H4>
        {data.tag && <div className={styles.card_header_tag}>{data.tag}</div>}
      </div>
      <Caption className={styles.card_header_caption}>{t(data.desc)}</Caption>
    </div>
  );
};

const EDUCATION_BTN = {
  //TODO: 占位
  [PlanType.FREE]: {
    label: '学生・教職員向け割引',
    link: '/education-plan',
  },
  [PlanType.PRO]: {
    label: '学生・教職員向け割引',
    link: '/education-plan',
  },
  [PlanType.TEAMS]: {
    label: 'ビジネス版教育割引申込',
    link: 'https://share.hsforms.com/1eZ-FMZtmS4GceU04mI3J1Anoue6',
  },
};

const PricingCardPrice = ({ data, gridArea }: IProp) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.price_btn_container} ${data.type === PlanType.PRO && styles.pro_price_container}`}
      style={{ gridArea, background: CARD_BG[data.type].body }}
    >
      <div className={styles.price_desc_container}>
        <div className={styles.price_unit_container}>
          <H2>
            {typeof data.averagePrice.ja.year === 'string'
              ? t(data.averagePrice.ja.year)
              : data.averagePrice.ja.year.toLocaleString()}
          </H2>
          {data.priceUnit && <Caption>{t(data.priceUnit.ja)}</Caption>}
        </div>
        <div className={styles.price_desc_month_container}>
          <div className={styles.price_desc_tag_container}>
            <Caption className={styles.pricing_desc_caption}>{t(data.priceDesc)}</Caption>
            {data.accountTag && <div className={styles.pricing_account_tag_container}>{data.accountTag}</div>}
          </div>
          {typeof data.averagePrice.ja.month === 'number' && (
            <Caption className={styles.pricing_desc_caption}>
              {data.averagePrice.ja.month.toLocaleString()}円・毎月払い
            </Caption>
          )}
        </div>
      </div>
      <div className={styles.upgrade_btn_container}>
        <a href={data.upgradeBtn.link} data-gtm={data.dataGtm}>
          <Button
            type={data.upgradeBtn.btnType}
            className={`${data.type === PlanType.PRO && styles.pro_upgrade_btn} ${
              data.type === PlanType.FREE && styles.free_upgrade_btn
            }`}
          >
            {t(data.upgradeBtn.text)}
          </Button>
        </a>
        <ButtonWithArrow
          link={EDUCATION_BTN[data.type].link}
          target="_blank"
          size="small"
          disable={data.type === PlanType.FREE}
          className={data.type === PlanType.FREE && styles.useless_btn}
        >
          {EDUCATION_BTN[data.type].label}
        </ButtonWithArrow>
      </div>
    </div>
  );
};

const PricingCardFeature = ({ data, gridArea }: IProp) => {
  const { t } = useTranslation();
  return (
    <div
      className={`${styles.features_container} ${data.type === PlanType.PRO && styles.pro_features_container}`}
      style={{ gridArea, background: CARD_BG[data.type].body }}
    >
      <Caption className={styles.feature_desc_caption}>{t(data.featureDesc)}</Caption>
      <div className={styles.features_list_container}>
        {data.featureList.map((item, index) => {
          return (
            <div key={index} className={styles.feature_item}>
              <PublicCheckFilled size={16} fill={data.type === PlanType.FREE ? '#7c838b' : '#4a87e9'} />
              <Caption className={styles.feature_item_caption}>{typeof item === 'string' ? t(item) : item}</Caption>
            </div>
          );
        })}
      </div>
    </div>
  );
};

// const PricingCom = () => {
//   return (
//     <CommonBlok title="料金プラン" isNewLayout>
//       <div className={styles.pricing_container}>
//         <div className={styles.blok_content}>
//           {[FreePlanInfo, ProPlanInfo, TeamsPlanInfo].map((item, index) => {
//             return <PricingCardHeader key={index} data={item} gridArea={'h' + (index + 1)} />;
//           })}
//           {[FreePlanInfo, ProPlanInfo, TeamsPlanInfo].map((item, index) => {
//             return <PricingCardPrice key={index} data={item} gridArea={'p' + (index + 1)} />;
//           })}
//           {[FreePlanInfo, ProPlanInfo, TeamsPlanInfo].map((item, index) => {
//             return <PricingCardFeature key={index} data={item} gridArea={'f' + (index + 1)} />;
//           })}
//         </div>
//         <div className={styles.pricing_page_btn}>
//           <a href="/pricing" data-gtm="home-page-pricing-to-pricing-page-btn">
//             <Button type="link">
//               プランの詳細を見る
//               <PublicArrowRightTail />
//             </Button>
//           </a>
//         </div>
//       </div>
//     </CommonBlok>
//   );
// };
const PricingCom = () => {
  return (
    <PricingProvider lang="ja">
      <CommonBlok
        title="料金プラン"
        isNewLayout
        maxWidth="100%"
        className={styles.blok_style}
        contentClass={styles.content_style}
      >
        <div className={styles.pricing_container}>
          <div className={styles.pricing_page_part}>
            <SwitchPayType />
            <PricingCardContainer />
          </div>
          <div className={styles.pricing_page_btn}>
            <a href="/pricing" data-gtm="home-page-pricing-to-pricing-page-btn">
              <Button type="link">
                プランの詳細を見る
                <PublicArrowRightTail />
              </Button>
            </a>
          </div>
        </div>
      </CommonBlok>
    </PricingProvider>
  );
};
export default PricingCom;
